import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Code2, Brain, CheckCircle2, FlaskConical, ArrowRight } from 'lucide-react';
import { isFinnishPage, getServicesBasePath } from '../../../lib/blogPaths';

const AI_TOOLS_EN = [
  { icon: Code2, title: 'Code Assistants', description: 'In-editor suggestions and pair programming with Copilot, Cursor, Cody, and Windsurf so developers stay in flow.' },
  { icon: Brain, title: 'AI Reasoning', description: 'Architecture, debugging, and design—using ChatGPT, Claude, and Gemini to think through problems before we code.' },
  { icon: CheckCircle2, title: 'Code Review', description: 'AI-powered analysis and suggestions for faster, consistent reviews and fewer issues in production.' },
  { icon: FlaskConical, title: 'Testing', description: 'Automated test generation and edge-case detection for fewer regressions and higher confidence at release.' },
];

const AI_TOOLS_FI = [
  { icon: Code2, title: 'Koodiavustajat', description: 'Editorin ehdotukset ja pariohjelmointi Copilot-, Cursor-, Cody- ja Windsurf-työkaluilla, jotta kehittäjät pysyvät flow-tilassa.' },
  { icon: Brain, title: 'Tekoälypäättely', description: 'Arkkitehtuuri, debuggaus ja suunnittelu—ChatGPT, Claude ja Gemini auttavat pohtimaan ongelmia ennen koodausta.' },
  { icon: CheckCircle2, title: 'Koodikatselmointi', description: 'Tekoälyavusteinen analyysi ja ehdotukset nopeampaan, tasaisempaan katselmointiin ja vähemmän ongelmia tuotannossa.' },
  { icon: FlaskConical, title: 'Testaus', description: 'Automatisoitu testien luonti ja reunatapausten tunnistus vähemmän regressioita ja luotettavampi julkaisu.' },
];

export function ServicesAIDifferentiationSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const aiTools = isFinnish ? AI_TOOLS_FI : AI_TOOLS_EN;
  const servicesBase = getServicesBasePath(isFinnish ? 'fi' : 'en');
  const ctaHref = `${servicesBase}#final-cta`;

  const heading = isFinnish ? 'Miten tekoäly tehostaa kehitystämme' : 'How AI Enhances Our Development';
  const intro = isFinnish
    ? 'Jokainen Idention kehittäjä käyttää tekoälyä päivittäisessä työssään—koodin kirjoittamiseen, katselmointiin ja testaukseen. Olemme läpinäkyviä käyttämistämme työkaluista ja siitä, miten ne auttavat toimittamaan parempaa ohjelmistoa nopeammin.'
    : "Every developer at Identio uses AI in their daily workflow—for writing, reviewing, and testing code. We're transparent about the tools we use and how they help us deliver better software, faster.";
  const closing = isFinnish
    ? 'Valitsemme teknologiat projektikohtaisesti, emme toimittajan perusteella. Tiimimme seuraa aktiivisesti alan kehitystä ja jakaa löydöksensä, joten saat aina käyttöösi parhaat saatavilla olevat ratkaisut.'
    : "We choose technologies project by project, not by vendor. Our team actively tracks industry developments and shares their discoveries, ensuring you always benefit from the best available solutions.";
  const ctaLabel = isFinnish ? 'Keskustellaan tekoälytehostetusta kehityksestä' : 'Talk to Us About AI-Enhanced Development';

  return (
    <section
      ref={ref}
      className="section-rhythm bg-navy text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-highlight rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="heading-2 mb-6 font-semibold text-white">
            {heading}
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {intro}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {aiTools.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
                className="ai-tool-card flex flex-col text-center"
              >
                <div className="flex justify-center mb-4">
                  <div className="ai-tool-icon flex-shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[15px] text-gray-300 leading-relaxed">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-3xl mx-auto mb-16"
        >
          <p className="text-lg leading-relaxed text-gray-300 text-center">
            {closing}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-12"
        >
          <Link
            href={ctaHref}
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            {ctaLabel}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
